import { useQuery } from "@apollo/client";
import { Box, Typography } from "@mui/material";
import _ from "lodash";
import { ReactElement } from "react";
import { useParams } from "react-router-dom";

import {
  GetOwnedPhoneNumberPoolDetailsQuery,
  GetOwnedPhoneNumberPoolDetailsQueryVariables,
} from "~frontend/__generated__/graphql";
import { AsyncWrapper } from "~frontend/components/AsyncWrapper";
import { FlexBox } from "~frontend/components/FlexBox";
import PageContainer from "~frontend/components/PageContainer";
import Table from "~frontend/components/Table";
import NumberStat from "~frontend/components/stats/NumberStat";
import { GET_OWNED_PHONE_NUMBER_POOL_DETAILS } from "~frontend/graphql/ownedPhoneNumberPool";
import { useBreadcrumbs } from "~frontend/providers/BreadcrumbsProvider";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";

import PhoneNumberPoolPurchaseControls from "./PhoneNumberPoolPurchaseControls";

export default function PhoneNumberPoolDetails(): ReactElement {
  const { subaccount, subaccountLink } = useSubaccount()!;

  const { poolId: maybePoolId } = useParams<{
    poolId: string;
  }>();
  const poolId = maybePoolId!;

  const { data, loading, error } = useQuery<
    GetOwnedPhoneNumberPoolDetailsQuery,
    GetOwnedPhoneNumberPoolDetailsQueryVariables
  >(GET_OWNED_PHONE_NUMBER_POOL_DETAILS, {
    variables: {
      input: {
        poolId,
        subaccountId: subaccount.id,
      },
    },
    pollInterval: 30 * 1000,
  });

  useBreadcrumbs([
    {
      label: "Phone Numbers",
      path: subaccountLink("/phone-numbers"),
    },
    ...(data?.getOwnedPhoneNumberPool
      ? [
          {
            label: data.getOwnedPhoneNumberPool.name,
            path: subaccountLink(
              `/phone-numbers/pools/${data.getOwnedPhoneNumberPool.id}`
            ),
          },
        ]
      : []),
  ]);

  return (
    <PageContainer>
      <AsyncWrapper loading={loading} errors={error} data={data}>
        {({ getOwnedPhoneNumberPool: pool }) => (
          <>
            <Typography variant="h1">{pool!.name}</Typography>
            <FlexBox justifyContent="center">
              <NumberStat value={_.sumBy(pool.areaCodeSummary, s => s.count)}>
                Phone Numbers
              </NumberStat>
            </FlexBox>

            <Table
              rows={pool.areaCodeSummary.map(s => ({
                id: s.areaCode || "n/a",
                count: s.count,
              }))}
              columns={[
                {
                  key: "areaCode",
                  title: "Area Code",
                  renderValue: row => row.id,
                },
                {
                  key: "count",
                  title: "Count",
                  renderValue: row => row.count.toLocaleString(),
                },
              ]}
              noFooter
              emptyState="No phone numbers"
            />

            <Box sx={{ my: 12 }}>
              <PhoneNumberPoolPurchaseControls pool={pool} />
            </Box>
          </>
        )}
      </AsyncWrapper>
    </PageContainer>
  );
}
